import { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Input, InputNumber, message, Modal, Row, Select, Spin, Tooltip } from 'antd';
import api from '@/api/b2bCommissionRates';
import businessAdminJobsApi from '@/api/businessAdminJobs';

/**
 * Admin control of how a B2B job's assembly cost is split. Of the total the partner keeps their
 * share, Faastaa takes its commission, and the tasker is paid whatever is left — so the three
 * always add to 100% and the tasker column is derived, never typed.
 *
 * Scope is (partner, category) with either side optional, and the most specific match wins. The
 * list is returned in that precedence order, so the row that would actually apply sits at the top.
 */

interface RateRow {
    b2bCommissionRateId: string;
    businessAdminUserId?: string;
    businessAdminDisplayName?: string;
    productCategory?: string;
    partnerSharePercent: number;
    faastaaCommissionPercent: number;
    taskerPercent: number;
}

interface BusinessAdminOption {
    businessAdminUserId: string;
    companyName?: string;
    email?: string;
    displayName?: string;
}

interface RateFormValues {
    businessAdminUserId?: string;
    productCategory?: string;
    partnerSharePercent: number;
    faastaaCommissionPercent: number;
}

const ANY_PARTNER = '';

const defaultFormValues: RateFormValues = {
    businessAdminUserId: ANY_PARTNER,
    productCategory: '',
    partnerSharePercent: 0,
    faastaaCommissionPercent: 30,
};

const B2BCommissionRates = () => {
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState<RateRow[]>([]);
    const [partnerOptions, setPartnerOptions] = useState<BusinessAdminOption[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm<RateFormValues>();

    const partnerShare = Form.useWatch('partnerSharePercent', form);
    const commission = Form.useWatch('faastaaCommissionPercent', form);
    const taskerShare = 100 - (Number(partnerShare) || 0) - (Number(commission) || 0);

    const getList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.getList();
            setTableData(Array.isArray(res.data) ? res.data : []);
        } catch (error: any) {
            message.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadPartners = useCallback(async () => {
        try {
            const res = await businessAdminJobsApi.getBusinessAdminOptions();
            setPartnerOptions(Array.isArray(res.data) ? res.data : []);
        } catch {
            // The partner dropdown only narrows a rate's scope; a rate can still be saved without it.
            setPartnerOptions([]);
        }
    }, []);

    useEffect(() => {
        getList();
        loadPartners();
    }, [getList, loadPartners]);

    const partnerLabel = (option: BusinessAdminOption) =>
        option.displayName || option.companyName || option.email || option.businessAdminUserId;

    const openCreateModal = () => {
        setEditingId('');
        form.setFieldsValue(defaultFormValues);
        setModalOpen(true);
    };

    const openEditModal = (row: RateRow) => {
        setEditingId(row.b2bCommissionRateId);
        form.setFieldsValue({
            businessAdminUserId: row.businessAdminUserId || ANY_PARTNER,
            productCategory: row.productCategory || '',
            partnerSharePercent: row.partnerSharePercent,
            faastaaCommissionPercent: row.faastaaCommissionPercent,
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        let values: RateFormValues;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        if (values.partnerSharePercent + values.faastaaCommissionPercent > 100) {
            message.error('Partner share plus Faastaa commission cannot exceed 100%');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                businessAdminUserId: values.businessAdminUserId || undefined,
                productCategory: values.productCategory?.trim() || undefined,
                partnerSharePercent: values.partnerSharePercent,
                faastaaCommissionPercent: values.faastaaCommissionPercent,
            };
            if (editingId) {
                await api.updateRate(editingId, payload);
            } else {
                await api.createRate(payload);
            }
            message.success(editingId ? 'Rate updated' : 'Rate added');
            setModalOpen(false);
            getList();
        } catch (error: any) {
            message.error(error);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (row: RateRow) => {
        Modal.confirm({
            title: 'Delete this rate?',
            content:
                'Jobs in this scope will fall back to the next most general rate. Jobs already raised keep the split they were created with.',
            okText: 'Delete',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await api.deleteRate(row.b2bCommissionRateId);
                    message.success('Rate deleted');
                    getList();
                } catch (error: any) {
                    message.error(error);
                }
            },
        });
    };

    const isDefaultRow = (row: RateRow) => !row.businessAdminUserId && !row.productCategory;

    return (
        <>
            <div className="page-main account">
                <div className="page-title">B2B Commission</div>
                <div className="select-bar">
                    <div className="select-bar-left" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <span style={{ color: '#667085' }}>
                            Of each job&apos;s assembly cost: the partner keeps their share, Faastaa takes its
                            commission, and the tasker is paid the rest. The most specific rate wins.
                        </span>
                    </div>
                    <Button
                        type="primary"
                        size="small"
                        style={{ height: 46, width: 180, borderRadius: 8, justifySelf: 'flex-end' }}
                        onClick={openCreateModal}
                    >
                        Add Rate
                    </Button>
                </div>

                {loading ? (
                    <div className="data_loading flex-center">
                        <Spin />
                    </div>
                ) : (
                    <div className="table">
                        {tableData.length ? (
                            <>
                                <div className="table-th">
                                    <Row>
                                        <Col span={6}>Partner</Col>
                                        <Col span={5}>Category</Col>
                                        <Col span={3}>Partner keeps</Col>
                                        <Col span={3}>Faastaa</Col>
                                        <Col span={3}>Tasker</Col>
                                        <Col span={4}>Actions</Col>
                                    </Row>
                                </div>
                                {tableData.map(row => (
                                    <Row className="table-tr" key={row.b2bCommissionRateId}>
                                        <Col span={6}>
                                            {row.businessAdminDisplayName || (
                                                <span style={{ color: '#98a2b3' }}>All partners</span>
                                            )}
                                        </Col>
                                        <Col span={5}>
                                            {row.productCategory || (
                                                <span style={{ color: '#98a2b3' }}>All categories</span>
                                            )}
                                        </Col>
                                        <Col span={3}>{row.partnerSharePercent}%</Col>
                                        <Col span={3}>{row.faastaaCommissionPercent}%</Col>
                                        <Col span={3}>{row.taskerPercent}%</Col>
                                        <Col span={4}>
                                            <Button type="link" onClick={() => openEditModal(row)}>
                                                Edit
                                            </Button>
                                            {isDefaultRow(row) ? (
                                                <Tooltip title="The default always has to exist — edit it instead.">
                                                    <Button type="link" disabled>
                                                        Delete
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Button type="link" danger onClick={() => handleDelete(row)}>
                                                    Delete
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                ))}
                            </>
                        ) : (
                            <div className="no-data flex-center">No data</div>
                        )}
                    </div>
                )}
            </div>

            {/*
                A global rule hides `.ant-modal-footer` for every modal in this app, so the actions
                have to live inside the body — same as the Add Job form.
            */}
            <Modal
                open={modalOpen}
                title={editingId ? 'Edit Rate' : 'Add Rate'}
                onCancel={() => setModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form form={form} layout="vertical" initialValues={defaultFormValues}>
                    <Form.Item label="Partner" name="businessAdminUserId">
                        <Select
                            options={[
                                { value: ANY_PARTNER, label: 'All partners' },
                                ...partnerOptions.map(option => ({
                                    value: option.businessAdminUserId,
                                    label: partnerLabel(option),
                                })),
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Product category"
                        name="productCategory"
                        extra="Leave blank to apply to every category."
                    >
                        <Input placeholder="e.g. Service" />
                    </Form.Item>
                    <Form.Item
                        label="Partner keeps (%)"
                        name="partnerSharePercent"
                        rules={[{ required: true, message: 'Enter the partner share' }]}
                    >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label="Faastaa commission (%)"
                        name="faastaaCommissionPercent"
                        rules={[{ required: true, message: 'Enter the Faastaa commission' }]}
                    >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                    <div style={{ color: taskerShare < 0 ? '#d92d20' : '#667085' }}>
                        {taskerShare < 0
                            ? `Over-allocated by ${-taskerShare}% — the tasker would be paid nothing.`
                            : `Tasker is paid the remaining ${taskerShare}%.`}
                    </div>

                    <Row gutter={16} className="job-form-footer" style={{ marginTop: 24 }}>
                        <Col span={12}>
                            <Button className="submit-btn cancel-btn" onClick={() => setModalOpen(false)}>
                                Close
                            </Button>
                        </Col>
                        <Col span={12}>
                            <Button type="primary" className="submit-btn" onClick={handleSave}>
                                {saving ? 'Saving…' : editingId ? 'Save' : 'Add Rate'}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

export default B2BCommissionRates;
